<template>
  <div class="app-content creating clearfix">
    <pointer-effect />
    <header-bar
      :show-teams="false"
      :show-save="true"
      :show-back="true"
      :show-shortcuts="true"
      :has-unsaved-changes="true"
      :disable-auto-save="disableAutoSave"
      data-test="graphics-headerBar"
      @save="save"
      @enable-auto-save="disableAutoSave = false"
      @auto-save="save({autoSave: true})"
      @modal="togglekeyboardShortcuts"
    />
    <div class="builder-main-content">
      <sidebar-left
        ref="sidebarLeft"
        :template="template"
        v-model:show-element-library="elementLibraryOpen"
        @modal="toggleAssetModal"
        @open-social-modal="showSocialModal = true"
        @open-sportradar-modal="openSportradarModal"
        @open-shoppable-modal="showShoppableModal = true"
        @open-scorestream-modal="showScorestreamModal = true"
        @open-foxforesight-modal="showFoxForesightModal = true"
        @open-google-sheets-modal="openGoogleSheetsModal"
        @preview="openPresetPreview"
      />

      <template-preview
        ref="prvTmp"
        :show-element-library="elementLibraryOpen"
      />

      <sidebar-right
        :template="template"
        @open-google-sheets-modal="openGoogleSheetsModal"
        @modal="toggleAssetModal"
      />
    </div>

    <!--TODO:
      While fixing some things for VUEX review, found this bit of code that doesn't
      fires on production either. Seems like something we should fix. -->
    <alert
      v-if="showAlert"
      data-test="graphics-alertUnsaved"
      @confirm="navigateHome"
      @close="showAlert = false"
    >
      <p>You have unsaved changes.</p>
      <p>
        Are you sure you want to exit the template builder? Any unsaved changes
        will be lost.
      </p>
    </alert>

    <modal
      v-if="assetUploaderOpen"
      id="asset-uploader-modal"
      data-test="graphics-assetUploader"
      :header="false"
      :footer="false"
    >
      <template #modal-body>
        <iframe
          id="asset-uploader"
          :src="assetManagerSrc"
          data-test="graphics-assetsUploaderIframe"
          height="100%"
          width="100%"
          style="width: 100%; height: 100%; border: 0"
        />

        <button
          v-if="assetUploaderOpen"
          data-test="graphics-assetUploaderClose"
          data-entity="graphics-assetUploaderClose"
          class="modal-close-alt"
          @click="assetUploaderOpen = false"
        >
          <span class="fa-solid fa-xmark" />
        </button>
      </template>
    </modal>

    <modal
      v-if="showFontManager"
      id="font-uploader-modal"
      data-test="graphics-fonts-uploader"
    >
      <template #modal-body>
        <iframe
          id="asset-uploader"
          data-test="graphics-fontsUploaderIframe"
          :src="fontManagerSrc"
          height="100%"
          width="100%"
          style="width: 100%; height: 100%; border: 0"
        />

        <button
          class="modal-close-alt"
          data-test="graphics-fontsUploaderClose"
          data-entity="graphics-fontsUploaderClose"
          @click="showFontManager = false"
        >
          <span class="fa-solid fa-xmark" />
        </button>
      </template>
    </modal>

    <social-modal
      v-if="showSocialModal"
      :social-template="socialTemplate"
      action="createFullScreenSocial"
      @close="showSocialModal = false"
      @template-selections="handleSocialTemplateSelections"
    />

    <sportradar-connect-modal
      v-if="showSportradarModal"
      :league-name="activeSportradarLeague"
      @close="showSportradarModal = false"
    />

    <shoppable-modal
      v-if="showShoppableModal"
      @close="showShoppableModal = false"
      @select="handleShoppableSelect"
    />

    <scorestream-modal
      v-if="showScorestreamModal"
      @close="showScorestreamModal = false"
      @connect="handleScorestreamConnect"
    />

    <foxforesight-modal
      v-if="showFoxForesightModal"
      @close="showFoxForesightModal = false"
      @connect="handleFoxForesightConnect"
    />

    <google-connect-modal
      v-if="showGoogleSheetsModal && googleDataElement && canvas"
      :element="googleDataElement"
      :canvas="canvas"
      @close="closeGoogleSheetsModal"
    />

    <modal
      v-if="showPresetPreview"
      :header="true"
      :footer="false"
      auto-height
      data-test="graphics-presetPreviewModal"
      :close-button="true"
      @close="closePresetPreview"
    >
      <template #modal-header>
        <h2 class="preset-preview__header">
          {{ previewPreset?.title }}
        </h2>
      </template>

      <template #modal-body>
        <div class="preset-preview__body">
          <img
            v-if="getPresetPreviewUrl(previewPreset)"
            :src="getPresetPreviewUrl(previewPreset)"
            :alt="previewPreset.title"
            class="preset-preview__image"
          >
        </div>
      </template>
    </modal>

    <keyboard-shortcuts
      v-if="keyboardShortcutsOpen"
      @modal="togglekeyboardShortcuts"
    />
    <footer-bar @fit="handleFit()" />
  </div>
</template>

<script>
import {
  mapState,
  mapActions,
  mapGetters,
} from 'vuex';

import { cloneDeep } from 'lodash-es';
import { useFormContext } from 'vee-validate';

import AppMixins from '../mixins/app-mixins';
import toast from '../utils/toasts';
import { inIframe } from '../utils/helpers';
import { allContentTypes } from '../utils/social';
import defaults from '../utils/defaults';

import KeyboardShortcutsComponent from './KeyboardShortcuts.vue';
import FooterBar from './TemplateFooter.vue';
import SocialModal from './Modal/SocialModal.vue';
import SportradarConnectModal from './SidebarLeft/Sportradar/SportradarConnectModal.vue';
import ShoppableModal from './Modal/ShoppableModal.vue';
import ScorestreamModal from './Modal/ScorestreamModal.vue';
import FoxForesightModal from './Modal/FoxForesightModal.vue';
import GoogleConnectModal from './Modal/GoogleGonnectModal.vue';
import Modal from './Modal/Modal.vue';

import ConduitEventsService from '../services/conduit';
import Packs from '../services/packs';

export default {
  setup() {
    const { validate } = useFormContext();
    return { validateForm: validate };
  },
  components: {
    'keyboard-shortcuts': KeyboardShortcutsComponent,
    FooterBar,
    SocialModal,
    SportradarConnectModal,
    ShoppableModal,
    ScorestreamModal,
    'foxforesight-modal': FoxForesightModal,
    GoogleConnectModal,
    Modal,
  },

  mixins: [AppMixins],

  provide() {
    return {
      toggleFontModal: () => {
        this.showFontManager = !this.showFontManager;
      },
    };
  },

  data() {
    return {
      showFontManager: false,
      disableAutoSave: false,
      elementLibraryOpen: false,
      previewPreset: null,
      showPresetPreview: false,
      showSocialModal: false,
      socialTemplate: {
        template: 'fullScreen',
        options: allContentTypes,
      },
      showSportradarModal: false,
      showShoppableModal: false,
      showScorestreamModal: false,
      showFoxForesightModal: false,
      showGoogleSheetsModal: false,
      activeSportradarLeague: null,
    };
  },

  computed: {
    ...mapGetters('graphicsTemplate', ['findElement']),

    ...mapState({
      partners: (state) => state.user.partners ?? [],
    }),

    ...mapGetters({
      currentIndex: 'getCurrentIndex',
      currentSavedStateIndex: 'getCurrentSavedStateIndex',
    }),

    ...mapGetters('graphicsTemplate', [
      'getCanvas',
      'getElementById',
    ]),

    canvas() {
      return this.getCanvas;
    },

    googleDataElement() {
      const selectedIds = (
        this.$store.state.templates
        && this.$store.state.templates.selectedElements
      ) || [];

      if (!selectedIds.length) return null;

      const el = this.getElementById(selectedIds[0]);
      if (!el || el.type !== 'sheet-data') return null;

      return el;
    },
  },

  async created() {
    const { provider, preset } = this.$route.query;

    await this.$store.dispatch('getPartners'); // Preload available partners and tokens

    if (!provider) {
      let fullTemplate;

      if (preset) {
        // Fetch from Packs & Templates API
        const templateData = await Packs.getTemplate(+preset);
        fullTemplate = templateData.settings;
      } else {
        fullTemplate = cloneDeep(defaults.template);
      }

      if (fullTemplate) {
        // new store value set
        await this.$store.dispatch('graphicsTemplate/setAllGraphicsTemplateState', {
          fullTemplate,
          options: { blacklist: true }, // SHOULD THIS ALWAYS BE BAKED INTO THE ACTION?
        });

        await this.setTemplateOwner({
          value: this.$store.state.user.owner,
          options: {
            blacklist: true,
          },
        });
      }

      // If loaded via a preset, check if the template contains a PDK experience we need to map.
      if (preset) {
        this.checkForPDKElement();
      }
    }

    this.$store.setInitialState();
  },

  methods: {
    ...mapActions('graphicsTemplate', ['setTemplateOwner', 'setOneElementValueByProp']),
    ...mapActions(['setCurrentSavedStateIndex']),

    async checkForPDKElement() {
      const el = this.findElement((e) => e.type === 'frame' && e.partner);
      if (!el) { return; }

      // Clear existing data on the PDK element, which should trigger the config modal to show.
      this.setOneElementValueByProp({
        value: {
          id: el.id,
          propKey: 'url',
          propValue: '',
        },
      });
    },

    async save({ autoSave = false } = {}) {
      this.$store.dispatch('startLoader');

      const { valid } = await this.validateForm();

      if (valid) {
        try {
          const template = await this.$store.dispatch('graphicsTemplate/transformGraphicsTemplateStateForSave');
          const id = await this.$store.dispatch(
            'createTemplate',
            template,
          );

          this.setCurrentSavedStateIndex(this.currentIndex);

          ConduitEventsService.track(
            'graphics.created.template',
            {
              template_id: id,
              title: template.title,
              template_template: this.$route.query.template,
              template_theme: this.$route.query.theme,
              ...this.postfixedElementCounts,
            },
            {
              ownerId: template.owner,
              userId: this.$store.state.user.user.id,
              isStaff: this.$store.state.user.user.is_staff,
            },
          );

          // Emit message out to any parents (if iframed).
          // That way the parent can handle routing instead.
          if (inIframe()) {
            try {
              window.parent.postMessage(`tgbGraphics:created:${id}`, '*');
            } catch (err) {
              // If failed to post message to parent, then continue to $router...
              this.$router.push({ name: 'edit', params: { template_id: id } });

              toast.makeToast({
                text: 'Template created successfully!',
              });
            }
          } else {
            // Not in an iframe, so use base routing
            this.$router.push({ name: 'edit', params: { template_id: id } });

            toast.makeToast({
              text: 'Template created successfully!',
            });
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log(error.responseText);

          if (autoSave) {
            this.disableAutoSave = true;
            toast.makeToast({
              heading: 'Error',
              icon: 'error',
              text: 'There was an error saving your Template. Autosave has been disabled to prevent error spamming.',
            });
          } else {
            toast.makeToast({
              heading: 'Error',
              icon: 'error',
              text: 'There was an error saving your Template. Please try again.',
            });
          }
        }
      }

      this.$store.dispatch('stopLoader');
    },

    handleFit() {
      this.$refs.prvTmp.centerContent();
      this.$nextTick(() => {
        this.$refs.prvTmp.resize();
      });
    },

    handleSocialTemplateSelections(payload) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.createSocialElement) {
        this.$refs.sidebarLeft.createSocialElement(payload);
      }
      this.showSocialModal = false;
    },

    openSportradarModal({ leagueName }) {
      this.activeSportradarLeague = leagueName || null;
      this.showSportradarModal = true;
    },

    handleShoppableSelect(selectedElements) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.addShoppableElement) {
        this.$refs.sidebarLeft.addShoppableElement(selectedElements);
      }
      this.showShoppableModal = false;
    },

    handleScorestreamConnect(config) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.addScorestreamElement) {
        this.$refs.sidebarLeft.addScorestreamElement(config);
      }
      this.showScorestreamModal = false;
    },

    handleFoxForesightConnect(config) {
      if (this.$refs.sidebarLeft && this.$refs.sidebarLeft.addFoxForesightElement) {
        this.$refs.sidebarLeft.addFoxForesightElement(config);
      }
      this.showFoxForesightModal = false;
    },

    openGoogleSheetsModal() {
      this.showGoogleSheetsModal = true;
    },

    closeGoogleSheetsModal() {
      this.showGoogleSheetsModal = false;
    },

    getPresetPreviewUrl(preset) {
      return preset?.posterUrl || preset?.thumbnailUrl || preset?.imageUrl || preset?.preview_url || '';
    },

    openPresetPreview(preset) {
      this.previewPreset = preset;
      this.showPresetPreview = true;
    },

    closePresetPreview() {
      this.showPresetPreview = false;
      this.previewPreset = null;
    },
  },
};
</script>
<style lang="scss">
  .preset-preview__body {
  display: flex;
  justify-content: center;
  align-items: center;
}

.preset-preview__image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}

.preset-preview__header {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
}
</style>
